/**
 * Feature E08-F03 — "Google Drive (файлы проектов + .docx)" request/response
 * contracts. 1:1 port of the n8n groups GDOCX (batch Google-Docs generation) +
 * DOCX (single-artifact .docx download) + Att (email-attachment transfer)
 * (docs/source-n8n/reference/{GDOCX,DOCX,Att}.md, integrations.md).
 *
 * Lives in its OWN file (NOT re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 *
 * Surface (mounted under `/projects` by the Wire pass):
 *
 *   POST /projects/:id/docx-package
 *        → generate the 4-artifact .docx package as Google Docs, share each
 *          anyone:reader and write the export?format=docx URL into
 *          project_artifacts.file_url. (T03, T03-S01, T03-S02, T04, T06)
 *        200 { ok, generated:[{ type, file_url }], message }
 *
 *   GET  /projects/:id/artifacts/:type/docx
 *        → on-demand single-artifact .docx (live DOCX group): render the stored
 *          artifact JSON → Google Doc → export .docx → cleanup temp doc → stream
 *          the binary back as an attachment. (T08)
 *        200 binary (application/vnd.openxmlformats-officedocument.wordprocessingml.document)
 *
 *   POST /projects/:id/manual-context/files  (multipart files[])
 *        → upload project files into the project's Drive folder
 *          (creating google_folder_id if absent), dedupe by name, merge into
 *          data.manual_context.files[] and return the merged list. (T01, T02)
 *        200 { ok, files:[{ name, url }], google_folder_id }
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Artifact doc types (the GDOCX/DOCX whitelist — closes the legacy SQL-string
// `pa.type = '{{ doc_type }}'` injection, DOCX.md gotcha #2).
// ---------------------------------------------------------------------------
/**
 * The 4 artifact types the batch .docx package renders, IN ORDER
 * (GDOCX.md: CASE a.type jd_doc=1, interview_questions=2, inmails=3,
 * sourcing_sheet=4). T03 acceptance #1.
 */
export const DOCX_PACKAGE_DOC_TYPES = [
    'jd_doc',
    'interview_questions',
    'inmails',
    'sourcing_sheet',
];
/**
 * Every doc type the single-download (DOCX group) can render. Superset of the
 * package set — includes kickoff_sheet / client_followup_email /
 * internal_notification (DOCX.md "DOCX: Build Requests" labels map). T08 whitelist
 * that replaces the legacy interpolated `pa.type = '{{ doc_type }}'`.
 */
export const DOCX_DOWNLOAD_DOC_TYPES = [
    'jd_doc',
    'interview_questions',
    'inmails',
    'sourcing_sheet',
    'kickoff_sheet',
    'client_followup_email',
    'internal_notification',
];
/** Human label per doc type (DOCX.md "labels{}" / GDOCX.md "label" map). */
export const DOCX_DOC_TYPE_LABEL = {
    jd_doc: 'Job Description',
    interview_questions: 'Interview Questions',
    inmails: 'InMails',
    sourcing_sheet: 'Sourcing Sheet',
    kickoff_sheet: 'Kickoff Sheet',
    client_followup_email: 'Follow-up Email',
    internal_notification: 'Internal Notification',
};
/** The .docx MIME type (Word). DOCX.md export mimeType + response Content-Type. */
export const DOCX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
/** Google Doc MIME type — Drive auto-converts the uploaded HTML to a native Doc. */
export const GOOGLE_DOC_MIME_TYPE = 'application/vnd.google-apps.document';
// ---------------------------------------------------------------------------
// POST /projects/:id/docx-package
// ---------------------------------------------------------------------------
/** One generated package row — the artifact type + its saved export URL. */
export const docxPackageRowSchema = z.object({
    type: z.enum(DOCX_PACKAGE_DOC_TYPES),
    file_url: z.string(),
});
/** GDOCX response: `{ success:true, message }` + the per-artifact file_urls. */
export const docxPackageResponseSchema = z.object({
    ok: z.literal(true),
    generated: z.array(docxPackageRowSchema),
    message: z.string(),
});
// ---------------------------------------------------------------------------
// GET /projects/:id/artifacts/:type/docx  (single download — params only)
// ---------------------------------------------------------------------------
/** Path params for the single-artifact .docx download (DOCX group, T08). */
export const docxDownloadParamsSchema = z.object({
    id: z.coerce.number().int().positive(),
    type: z.enum(DOCX_DOWNLOAD_DOC_TYPES),
});
// ---------------------------------------------------------------------------
// POST /projects/:id/manual-context/files  (multipart upload result)
// ---------------------------------------------------------------------------
/** A file reference in data.manual_context.files (Att.md: `{ name, url }`). */
export const driveFileRefSchema = z
    .object({
    name: z.string(),
    url: z.string(),
})
    .passthrough();
/** Upload result: merged files[] + the (possibly freshly created) folder id. */
export const manualContextUploadResponseSchema = z.object({
    ok: z.literal(true),
    files: z.array(driveFileRefSchema),
    google_folder_id: z.string().nullable(),
});
/** Build the canonical Google Docs `export?format=docx` URL from a file id. */
export function buildDocxExportUrl(fileId) {
    return `https://docs.google.com/document/d/${fileId}/export?format=docx`;
}
//# sourceMappingURL=google-drive.js.map